import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useRecoilValue } from 'recoil';
import type { RoiId } from '~/domain/changes/ids';
import {
    ContourEditBlocker,
    resolveContourEditBlocker,
} from '~/domain/dicom/contourEditBlocker';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import {
    bulkRoiSelectionActiveState,
    selectedRoiIsPointTypeState,
} from '~/state/atoms/structureSet/roiAtoms';
import { useActiveEditTarget } from './useActiveEditTarget';

export interface StructureToolGating {
    selectedStructureSet: StructureSetReadModel | undefined;
    selectedRoiId: RoiId | null;
    selectedRoi: RoiReadModel | undefined;
    isPointRoi: boolean;
    isTilted: boolean;
    /** The structure set is approved (rejects creating or modifying ROIs). */
    isStructureSetApproved: boolean;
    /**
     * Which of the two guards refuses a geometry edit of the selection, or
     * undefined when it is writable. Approval outranks the lock, and this is
     * the single predicate for "the selection is protected".
     */
    selectionEditBlocker: ContourEditBlocker | undefined;
    isBulkSelectionActive: boolean;
    /**
     * The primary image is not the whole volume: slices are still arriving, or
     * retrieval ended short and never will complete (SR-WOR-H-37-3).
     *
     * Contouring on a partially arrived volume is an unsafe act in both
     * directions — the anatomy the user is drawing against may not be there
     * yet, and the slices that arrive afterwards were never reviewed. Both
     * states gate; an incomplete-final image stays gated permanently, because
     * waiting cannot fix it.
     */
    isPrimaryImageIncomplete: boolean;
    /** Localised reason for {@link isPrimaryImageIncomplete}, else undefined. */
    imageIncompleteReason: string | undefined;
    /** A structure set with at least one ROI is loaded. */
    hasStructures: boolean;
    /** A concrete ROI is selected (may be a point ROI). */
    hasSelectedRoi: boolean;
    /**
     * Editing the selected ROI on whatever plane a view shows is unavailable
     * (deform / smoothing brush). Every gate of contour editing except the
     * tilted-grid one, which those strokes do not depend on.
     */
    selectedRoiAnyPlaneEditDisabled: boolean;
    /**
     * In-place contour editing of the selected ROI is unavailable — drawing
     * tools (brush/line/erase/line-deform) and slice operations
     * (clear/interpolate) all share this gate.
     */
    selectedRoiContourEditDisabled: boolean;
    /**
     * Structure-set-level editing is unavailable — operations that need a
     * writable, non-point structure set (boolean/margin) and undo/redo.
     */
    structureSetEditDisabled: boolean;
    /**
     * The selected structure set's dataset has no resident Environment
     * (fail-safe; see {@link useActiveEditTarget}). A write tool
     * armed against this target falls back to pan with no visible effect, so
     * this is OR'd into both edit-disabled gates rather than left for a
     * consumer to notice on its own.
     */
    targetUnresolved: boolean;
}

/**
 * Single source of the selection/approval/image flags that gate ROI-editing
 * toolbar controls, so each control composes its disabled state from shared
 * primitives instead of re-reading the same atoms.
 */
export function useStructureToolGating(): StructureToolGating {
    // Structure set + ROI come from the one edit-target resolver so every gate
    // retargets with it; the selection is global.
    const {
        environment,
        structureSet: selectedStructureSet,
        roiId: selectedRoiId,
    } = useActiveEditTarget();
    const isPointRoi = useRecoilValue(selectedRoiIsPointTypeState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    // The edit target's own image, so the gate judges the image the tools
    // write into.
    const primaryImage = environment?.image?.parent;
    const imageGate = useImageCompletenessGate(primaryImage);

    const selectedRoi = selectedStructureSet?.roiArray.find(
        (roi) => roi.roiId === selectedRoiId
    );

    // Fail-safe term: a selection whose dataset has no resident Environment
    // has nothing on screen to write to, so it must gate every consumer.
    const targetUnresolved = environment === undefined;
    // An unresolved Environment must never read as "not tilted"; that
    // would silently drop this into the false branch of every isTilted
    // check below instead of blocking them.
    const isTilted = targetUnresolved || !!primaryImage?.firstSlice.tilted;
    const hasStructures =
        !!selectedStructureSet && selectedStructureSet.roiArray.length > 0;
    const hasSelectedRoi = hasStructures && selectedRoiId !== null;
    const isStructureSetApproved = !!selectedStructureSet?.isApproved;
    const selectionEditBlocker = resolveContourEditBlocker(
        isStructureSetApproved || !!selectedRoi?.isApproved,
        !!selectedRoi?.locked
    );
    const selectedRoiAnyPlaneEditDisabled =
        !hasSelectedRoi ||
        isPointRoi ||
        selectionEditBlocker !== undefined ||
        isBulkSelectionActive ||
        imageGate.isBlocked ||
        targetUnresolved;

    return {
        selectedStructureSet,
        selectedRoiId,
        selectedRoi,
        isPointRoi,
        isTilted,
        isStructureSetApproved,
        selectionEditBlocker,
        isBulkSelectionActive,
        isPrimaryImageIncomplete: imageGate.isBlocked,
        imageIncompleteReason: imageGate.reason,
        hasStructures,
        hasSelectedRoi,
        targetUnresolved,
        selectedRoiAnyPlaneEditDisabled,
        selectedRoiContourEditDisabled:
            selectedRoiAnyPlaneEditDisabled || isTilted,
        structureSetEditDisabled:
            !hasStructures ||
            isPointRoi ||
            isBulkSelectionActive ||
            imageGate.isBlocked ||
            targetUnresolved,
    };
}
